import { useCallback, useEffect, useState } from "react";

const EMBEDDED_MESSAGING_URL =
    "https://page-java-8991--asabuild.sandbox.my.site.com/ESWBAXMessageSettings1791289573888";
const ORG_ID = "00DW4000009jeYn";
const DEPLOYMENT_NAME = "BAX_Message_Settings";
const SCRT2_URL =
    "https://page-java-8991--asabuild.sandbox.my.salesforce-scrt.com";
const HIDE_BUTTON_EVENTS = [
    "onEmbeddedMessagingButtonCreated",
    "onEmbeddedMessagingWindowMinimized",
    "onEmbeddedMessagingWindowClosed",
];

// Salesforce owns page-level DOM and must only be initialized once, even on remount.
let initialization;

function initializeMessaging() {
    if (initialization) return initialization;

    initialization = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        const hideDefaultButton = () => {
            setTimeout(() => {
                window.embeddedservice_bootstrap?.utilAPI?.hideChatButton?.();
            }, 0);
        };
        const fail = (error) => {
            window.removeEventListener("onEmbeddedMessagingReady", handleReady);
            HIDE_BUTTON_EVENTS.forEach((name) =>
                window.removeEventListener(name, hideDefaultButton)
            );
            script.remove();
            reject(error);
        };
        const handleReady = () => {
            try {
                window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
                    HCP_Email: "john.doe@astrazeneca.com",
                    HCP_First_Name: "John",
                    HCP_Last_Name: "Doe",
                    HCP_Market_Code: "EG",
                });
                window.removeEventListener("onEmbeddedMessagingReady", handleReady);
                resolve();
            } catch (error) {
                fail(error);
            }
        };

        script.src = `${EMBEDDED_MESSAGING_URL}/assets/js/bootstrap.min.js`;
        script.async = true;
        script.onload = () => {
            try {
                const bootstrap = window.embeddedservice_bootstrap;
                bootstrap.settings.language = "en_US";
                bootstrap.settings.hideChatButtonOnLoad = true;
                bootstrap.settings.disableInlineAutoLaunch = true;
                bootstrap.init(ORG_ID, DEPLOYMENT_NAME, EMBEDDED_MESSAGING_URL, {
                    scrt2URL: SCRT2_URL,
                });
            } catch (error) {
                fail(error);
            }
        };
        script.onerror = () => fail(new Error("Failed to load Embedded Messaging"));
        window.addEventListener("onEmbeddedMessagingReady", handleReady);
        HIDE_BUTTON_EVENTS.forEach((name) =>
            window.addEventListener(name, hideDefaultButton)
        );
        document.body.appendChild(script);
    }).catch((error) => {
        initialization = undefined;
        console.error("Error loading Embedded Messaging:", error);
        throw error;
    });

    return initialization;
}

export default function useEmbeddedMessaging() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        initializeMessaging().then(
            () => {
                if (active) setLoading(false);
            },
            (error) => {
                if (active) {
                    setError(error?.message || "Failed to initialize Embedded Messaging");
                    setLoading(false);
                }
            }
        );
        return () => {
            active = false;
        };
    }, []);

    const launchChat = useCallback(async () => {
        try {
            await window.embeddedservice_bootstrap.utilAPI.launchChat();
        } catch (error) {
            console.error("Unable to launch chat:", error);
            setError(error?.message || "Unable to launch chat");
        }
    }, []);

    return { loading, error, launchChat };
}
